import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import type { RdTimeBlockWeekResponse } from "../../../shared/api-types.js";
import { authenticatedApi } from "./api.js";
import { blockTimeApiPath } from "./rd-block-time.js";

/** The zone changes only when the RD saves hours, so a minute is fresh enough. */
const ZONE_STALE_MS = 60_000;

export type RdBlockTimeZone =
  | { status: "loading" }
  | { status: "error"; retry: () => void }
  | { status: "ready"; timeZone: string };

/**
 * NO-877: the zone the blocks API reads a block's times in (the RD's saved
 * hours zone, else the practice zone), read from the same API the save goes
 * to. The Week read cannot answer this: it reports no zone for a week with no
 * hours in effect, while the blocks API still reads the saved one. The dialog
 * asks when it opens and shows a retry when the answer fails.
 */
export function useRdBlockTimeZone(rdId: string | null): RdBlockTimeZone {
  const { getToken } = useAuth();
  const query = useQuery({
    queryKey: ["rd-block-time-zone", rdId],
    queryFn: () =>
      authenticatedApi<{ data: RdTimeBlockWeekResponse }>(
        getToken,
        blockTimeApiPath(rdId)
      ),
    staleTime: ZONE_STALE_MS,
  });

  if (query.data) return { status: "ready", timeZone: query.data.data.timeZone };

  if (query.isError) return { status: "error", retry: () => void query.refetch() };

  return { status: "loading" };
}
